import { CardShell, EmptyState, ListSkeleton, Table, TableBody } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { notification } from '@/notifications/notification-store'
import { type AdminConcernRow, fileService } from '@/services/file.service'
import { ConcernRow, ConcernTableHeader, FileHeaderRow } from './concern-table'
import { type FileConcernGroup, groupConcernsByFile, SortDirection, type SortKey, sortGroups } from './group-by-file'

/**
 * SUPER_ADMIN diagnostics for file-processing concerns.
 *
 * Exists because a concern that exhausts its SQS retries lands in the DLQ and
 * is then invisible — nothing re-processes it and nothing reports it, so today
 * the first signal is a support ticket. This lists every concern across files,
 * flags the stale ones using the same cutoffs the stall sweep applies, and
 * offers the same per-concern reprocess an end user's own retry button calls.
 *
 * Ops-facing, following `RealtimeDiagnostics`'s precedent — not a polished
 * end-user surface.
 */

const STATUS_FILTERS = ['all', 'pending', 'running', 'completed', 'failed'] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

/**
 * Relative windows rather than a date picker: this page is used to answer "what
 * just happened", and the table grows by up to four rows per upload, so an
 * unbounded view is mostly noise. `null` means no time filter.
 */
const TIME_WINDOWS: readonly { label: string; minutes: number | null }[] = [
	{ label: 'any time', minutes: null },
	{ label: 'last 5 min', minutes: 5 },
	{ label: 'last 30 min', minutes: 30 },
	{ label: 'last 2 h', minutes: 120 },
	{ label: 'last 24 h', minutes: 1440 },
]

const CONCERN_QUERY_KEY = 'admin-file-processing-concerns'

/** Matches the endpoint's own default, so the first render asks for what it would send anyway. */
const CONCERN_PAGE_SIZE = 50

/** The endpoint's hard ceiling — asking beyond it is silently clamped server-side. */
const CONCERN_MAX_LIMIT = 200

/** The filter bar: status, time window, free-text file match, and un-hide. */
function Filters({
	status,
	onStatus,
	staleOnly,
	onStaleOnly,
	sinceMinutes,
	onSinceMinutes,
	fileFilter,
	onFileFilter,
	hiddenCount,
	onShowHidden,
}: {
	status: StatusFilter
	onStatus: (value: StatusFilter) => void
	staleOnly: boolean
	onStaleOnly: (next: (prev: boolean) => boolean) => void
	sinceMinutes: number | null
	onSinceMinutes: (value: number | null) => void
	fileFilter: string
	onFileFilter: (value: string) => void
	hiddenCount: number
	onShowHidden: () => void
}) {
	return (
		<>
			<div className="flex flex-wrap items-center gap-ds-2">
				{STATUS_FILTERS.map((value) => (
					<Button key={value} variant={status === value ? 'secondary' : 'muted'} onPress={() => onStatus(value)}>
						{value}
					</Button>
				))}
				<Button variant={staleOnly ? 'secondary' : 'muted'} onPress={() => onStaleOnly((prev) => !prev)}>
					stale only
				</Button>
			</div>

			<div className="flex flex-wrap items-center gap-ds-2">
				{TIME_WINDOWS.map((window) => (
					<Button
						key={window.label}
						variant={sinceMinutes === window.minutes ? 'secondary' : 'muted'}
						onPress={() => onSinceMinutes(window.minutes)}
					>
						{window.label}
					</Button>
				))}
				<input
					type="search"
					value={fileFilter}
					onChange={(event) => onFileFilter(event.target.value)}
					placeholder="Filter by file name or id"
					aria-label="Filter by file name or id"
					className="min-w-64 rounded-ds-control border border-ds-border bg-ds-surface px-ds-2 py-ds-1 text-ds-sm text-ds-text"
				/>
				{hiddenCount > 0 && (
					<Button variant="muted" onPress={() => onShowHidden()}>
						Show {hiddenCount} hidden
					</Button>
				)}
			</div>
		</>
	)
}

/**
 * Says how much of the backlog is on screen, and offers to load the rest.
 *
 * Without it a page of rows reads as the entire backlog — and the name/id
 * filter is applied to the loaded rows only, so searching for a file beyond the
 * cut answered "no results" rather than "not loaded yet".
 */
function TruncationFooter({
	loaded,
	total,
	limit,
	isFetching,
	onLoadMore,
}: {
	loaded: number
	total: number
	limit: number
	isFetching: boolean
	onLoadMore: () => void
}) {
	const hasMore = loaded < total
	const atCeiling = limit >= CONCERN_MAX_LIMIT

	return (
		<div className="flex items-center gap-ds-3">
			<p className="text-ds-sm text-ds-text-muted">
				Showing {loaded} of {total}
				{hasMore ? ' — filters above apply only to the rows loaded so far.' : '.'}
			</p>
			{hasMore && !atCeiling && (
				<Button variant="muted" isDisabled={isFetching} onPress={onLoadMore}>
					{isFetching ? 'Loading…' : 'Load more'}
				</Button>
			)}
			{hasMore && atCeiling && (
				<span className="text-ds-sm text-ds-text-muted">Limit reached — narrow the filters to see the rest.</span>
			)}
		</div>
	)
}

/**
 * The grouped, sortable concern table.
 *
 * One `tbody` per file, so the grouping survives sorting: a file's header row
 * and its concerns move together, and sorting reorders groups rather than
 * shuffling concerns between files.
 */
function ConcernTable({
	groups,
	sortKey,
	sortDirection,
	onSort,
	isReprocessing,
	onReprocess,
	onDismiss,
}: {
	groups: FileConcernGroup[]
	sortKey: SortKey
	sortDirection: SortDirection
	onSort: (key: SortKey) => void
	isReprocessing: boolean
	onReprocess: (row: AdminConcernRow) => void
	onDismiss: (resultId: string) => void
}) {
	return (
		<CardShell className="bg-ds-surface p-0">
			<Table className="text-left">
				<ConcernTableHeader sortKey={sortKey} sortDirection={sortDirection} onSort={onSort} />
				{groups.map((group) => (
					<TableBody key={group.fileId}>
						<FileHeaderRow group={group} />
						{group.concerns.map((row) => (
							<ConcernRow
								key={row.resultId}
								row={row}
								isReprocessing={isReprocessing}
								onReprocess={onReprocess}
								onDismiss={onDismiss}
							/>
						))}
					</TableBody>
				))}
			</Table>
		</CardShell>
	)
}

export function FileProcessingDiagnosticsPage() {
	const [status, setStatus] = useState<StatusFilter>('all')
	const [staleOnly, setStaleOnly] = useState(false)
	const [sinceMinutes, setSinceMinutes] = useState<number | null>(null)
	const [fileFilter, setFileFilter] = useState('')
	// Dismissals are local and deliberately not persisted: hiding a diagnostics
	// row is a way to work through a list, not a state worth keeping.
	const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set())
	// Newest-first by default, matching what the endpoint returns — the page is a
	// "what just happened" view before it is an analysis one.
	const [sortKey, setSortKey] = useState<SortKey>('latest')
	const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.DESC)
	// Grows in place rather than paging: an operator working through a backlog
	// wants one list to sort and group across, not slices that reset the view.
	// Bounded by the endpoint's own max, so it cannot ask for an unbounded scan.
	const [limit, setLimit] = useState(CONCERN_PAGE_SIZE)
	const queryClient = useQueryClient()

	const concerns = useQuery({
		queryKey: [CONCERN_QUERY_KEY, status, staleOnly, sinceMinutes, limit],
		queryFn: () =>
			fileService.listConcernsForAdmin({
				status: status === 'all' ? undefined : status,
				staleOnly,
				sinceMinutes: sinceMinutes ?? undefined,
				limit,
			}),
	})
	const total = concerns.data?.total ?? 0
	const loaded = concerns.data?.rows.length ?? 0

	const reprocess = useMutation({
		mutationFn: (row: AdminConcernRow) => fileService.reprocessConcern(row.fileId, row.concernId),
		onSuccess: (_data, row) => {
			notification.success(`Reprocess requested for ${row.concernId}.`)
			void queryClient.invalidateQueries({ queryKey: [CONCERN_QUERY_KEY] })
		},
		onError: (error: Error) => notification.error(`Reprocess failed: ${error.message}`),
	})

	// The file filter is applied client-side: it matches on name OR id, and the
	// server only knows the id — filtering there would miss a name search.
	const needle = fileFilter.trim().toLowerCase()
	const rows = (concerns.data?.rows ?? [])
		.filter((row) => !dismissed.has(row.resultId))
		.filter(
			(row) =>
				needle === '' ||
				row.fileId.toLowerCase().includes(needle) ||
				(row.fileName ?? '').toLowerCase().includes(needle),
		)
	// Grouped after filtering, so hiding a row can empty a group out of the list
	// entirely rather than leaving a file node with no concerns under it.
	const groups = sortGroups(groupConcernsByFile(rows), sortKey, sortDirection)

	// Re-clicking a column flips it; a new column starts descending, which is the
	// useful default for every one of them (newest, worst-first, most concerns).
	const toggleSort = (key: SortKey) => {
		if (key === sortKey) {
			setSortDirection((current) => (current === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC))
			return
		}
		setSortKey(key)
		setSortDirection(SortDirection.DESC)
	}

	return (
		<div className="flex flex-col gap-ds-4 p-ds-4">
			<header>
				<h1 className="font-ds-display text-ds-xl text-ds-text">File processing</h1>
				<p className="text-ds-sm text-ds-text-muted">
					Every post-upload concern across files. “Stale” uses the same cutoffs the stall sweep applies — a row that has
					outlived every delivery attempt SQS was going to make. Reprocess triggers the same endpoint a user’s own retry
					does.
				</p>
			</header>

			<Filters
				status={status}
				onStatus={setStatus}
				staleOnly={staleOnly}
				onStaleOnly={setStaleOnly}
				sinceMinutes={sinceMinutes}
				onSinceMinutes={setSinceMinutes}
				fileFilter={fileFilter}
				onFileFilter={setFileFilter}
				hiddenCount={dismissed.size}
				onShowHidden={() => setDismissed(new Set())}
			/>

			{concerns.isPending && <ListSkeleton label="Loading…" />}
			{concerns.isError && <p className="text-ds-sm text-ds-danger">Failed to load concerns.</p>}
			{!concerns.isPending && !concerns.isError && rows.length === 0 && (
				<EmptyState title="No concerns match this filter." variant="filtered" />
			)}

			{groups.length > 0 && (
				<ConcernTable
					groups={groups}
					sortKey={sortKey}
					sortDirection={sortDirection}
					onSort={toggleSort}
					isReprocessing={reprocess.isPending}
					onReprocess={(target) => reprocess.mutate(target)}
					onDismiss={(resultId) => setDismissed((prev) => new Set(prev).add(resultId))}
				/>
			)}

			{!concerns.isPending && !concerns.isError && total > 0 && (
				<TruncationFooter
					loaded={loaded}
					total={total}
					limit={limit}
					isFetching={concerns.isFetching}
					onLoadMore={() => setLimit((current) => Math.min(current + CONCERN_PAGE_SIZE, CONCERN_MAX_LIMIT))}
				/>
			)}
		</div>
	)
}

export default FileProcessingDiagnosticsPage
